import type { UserRef } from '../../../types/user'
import {
  ProcessSubjectTopic as TOPIC_CODES,
  type DeadlineFilter,
  type OccurrenceSortKey,
} from '@/types/shared-enums.generated'

export interface ProcessSubjectAttachedDocument {
  id: number
  name: string
  type?: string | null
  base_type?: string | null
}

export type ProcessSubjectTopic = TOPIC_CODES

/** Todo topic que não é `problemas` — também é o conjunto de categorias de
 * uma ocorrência (`occurrence_meta.categoria`). Espelha
 * `PROCESS_SUBJECT_CATEGORIES` no backend. */
export type ProcessSubjectCategory = Exclude<ProcessSubjectTopic, 'problemas'>

/**
 * Topics com label de exibição, na ordem em que aparecem nas abas. Fonte
 * única: usada pelas abas do chat (`TopicTabs`/`SubjectsList`), pelo select
 * de categoria da ocorrência e pela tela de tipos de usuário externo.
 */
const TOPIC_LABELS: Record<ProcessSubjectTopic, string> = {
  geral: 'Geral',
  documentacao: 'Documentação',
  transporte: 'Transporte',
  financeiro: 'Financeiro',
  lpco: 'LPCO',
  catalogo: 'Catálogo',
  aduaneiro: 'Aduaneiro',
  problemas: 'Ocorrências',
}

export const TOPICS: readonly { code: ProcessSubjectTopic; label: string }[] =
  Object.values(TOPIC_CODES).map((code) => ({ code, label: TOPIC_LABELS[code] }))

/** Categorias (tudo menos `problemas`) — derivado de `TOPICS` pra não repetir. */
export const SUBJECT_CATEGORIES = TOPICS.filter(
  (t): t is { code: ProcessSubjectCategory; label: string } =>
    t.code !== 'problemas',
)

export type ProcessSubjectStatus = 'open' | 'done'

export type ProcessSubjectMessageKind = 'user' | 'system'

export type ProcessSubjectMessageIcon =
  | 'doc'
  | 'truck'
  | 'money'
  | 'bot'
  | 'alert'

export interface OccurrenceMeta {
  categoria: ProcessSubjectCategory
  prazo: string
  descricao: string
  attached_documents?: ProcessSubjectAttachedDocument[]
}

export interface ProcessSubject {
  id: number
  process_id: number
  topic: ProcessSubjectTopic
  title: string
  created_by_id: number
  created_by?: UserRef | null
  responsible_id: number | null
  responsible?: UserRef | null
  status: ProcessSubjectStatus
  completed_at: string | null
  completed_by_id: number | null
  completed_by?: UserRef | null
  occurrence_meta: OccurrenceMeta | null
  created_at: string
  updated_at: string
  /** Timestamp da última mensagem, eventos inclusive (virtual, populado
   * pelo backend na listagem). */
  last_message_at?: string | null
  /** Data e começo do texto da última mensagem de pessoa — sem a abertura
   * automática nem eventos (o card da lista). */
  last_user_message_at?: string | null
  last_user_message_preview?: string | null
  /**
   * Quantidade de mensagens deste subject ainda não lidas pelo usuário atual.
   * Virtual, populado pelo backend na listagem. Conta apenas mensagens de
   * outros autores (não system, não próprias) com `created_at` posterior ao
   * `process_reads.last_read_at` do user/processo.
   */
  unread_count?: number
}

export interface ProcessSubjectMessage {
  id: number
  subject_id: number
  process_id: number
  kind: ProcessSubjectMessageKind
  author_id: number | null
  author?: UserRef | null
  content: string
  system_icon: ProcessSubjectMessageIcon | null
  system_payload: Record<string, unknown> | null
  attachments: Array<{
    gcsPath: string
    name: string
    size: number
    type: string
    /** Signed URL `GET` (5min) hidratada pelo backend ao servir a mensagem. */
    url?: string
  }>
  /**
   * ID da mensagem que esta está respondendo/citando (estilo WhatsApp).
   * `null` quando não é uma resposta. A original é resolvida em render-time
   * por lookup no array de mensagens já carregado (`useMessages`).
   */
  reply_to_message_id?: number | null
  created_at: string
  mentions?: ProcessSubjectMention[]
  /**
   * Soft delete. Quando preenchido, o backend já devolveu `content: ''` e
   * `attachments: []` — a thread mostra só "Mensagem apagada". A linha segue
   * no banco com o conteúdo original, para auditoria.
   */
  deleted_at?: string | null
  deleted_by?: UserRef | null
  /** Conteúdo original. Só vem para quem tem DevTools (revelar na thread). */
  deleted_content?: string | null
  /** Nomes dos anexos da mensagem apagada. Também só para DevTools. */
  deleted_attachment_names?: string[]
}

export interface ProcessSubjectMention {
  id: number
  message_id: number
  subject_id: number
  process_id: number
  mentioned_account_id: number
  mentioned_user?: UserRef | null
  read_at: string | null
  created_at: string
}

export interface ProcessFollower {
  id: number
  process_id: number
  account_id: number
  user?: UserRef | null
  created_at: string
}

export interface ProcessRead {
  id: number
  process_id: number
  account_id: number
  last_read_at: string
  updated_at: string
}

export interface ProcessListItem {
  id: number
  process_number: string | null
  company: string | null
  /** Empresa dona do processo (`processes.company_id`). */
  company_id: number | null
  status: string
  status_processo: string | null
  priority_level: string
  cost_center_id: number
  cost_center_name: string | null
  etd: string | null
  eta_inicial: string | null
  eta_atual: string | null
  data_coleta_recinto: string | null
  /** Timestamp da última mensagem (de qualquer subject) — exibido no card. */
  last_message_at: string | null
  unread_count: number
  has_mention_history: boolean
  has_pending_mention: boolean
  has_open_occurrence: boolean
  /** Quantas ocorrências em andamento o processo tem. */
  open_occurrence_count: number
  /** Prazo mais urgente entre as ocorrências em andamento (texto do meta). */
  next_deadline: string | null
  /** Responsáveis das ocorrências em andamento. */
  responsibles: Array<{ id: number; name: string | null }>
  /**
   * Problema do HUB (falha do sistema) ainda não resolvido neste processo.
   * Selo à parte do de ocorrência — não entra no card "Com ocorrências" nem
   * no filtro dele, porque o dono e o fluxo são outros (time de dev).
   * Sempre `false` para externos.
   */
  has_open_system_issue: boolean
  is_critical: boolean
  is_late: boolean
}

/** Filtros de escopo das listas de Ocorrências — valem para as duas visões
 * e para os contadores. Vazio = sem restrição. */
export interface ListScopeFilter {
  /** Seletor de empresas; vazio = todas as do vínculo. */
  companyIds: number[]
  /** Nomes escolhidos por tipo de analista; vazio = todos. */
  analysts: AnalystSelection
}

/**
 * Os dois analistas do processo: `client` = analista do cliente (usuário de
 * perfil cliente da empresa, ou o nome vindo da Narwal); `responsible` =
 * analista responsável pelo despacho.
 */
export type AnalystKind = 'client' | 'responsible'
export type AnalystSelection = Record<AnalystKind, string[]>

/** Sem analista escolhido (também o formato das opções vazias). */
export const NO_ANALYSTS: AnalystSelection = { client: [], responsible: [] }

export interface ProcessListResponse {
  items: ProcessListItem[]
  totals: {
    total_processes: number
    total_unread: number
    with_open_occurrence: number
    critical: number
    late: number
    in_progress: number
    responsible: number
  }
  page: number
  limit: number
  has_more: boolean
}

/** Visão "por ocorrência" — `GET /occurrences/occurrence-list`. */
export type OccurrenceListStatus = 'open' | 'done' | 'all'
export type OccurrenceListFilter = 'all' | 'responsible' | 'overdue' | 'unread'

/** Situação de prazo (filtro "Prazo" da lista de processos) — do backend. */
export type { DeadlineFilter }

/** Filtros da visão por processo que combinam com o bloco escolhido. */
export interface ProcessExtraFilters {
  deadline: DeadlineFilter | null
  /** Etapas (`status_processo`) escolhidas; vazio = todas. */
  stages: string[]
  /** "Só processos com ocorrência aberta". */
  onlyOpen: boolean
}

export const NO_PROCESS_EXTRA_FILTERS: ProcessExtraFilters = {
  deadline: null,
  stages: [],
  onlyOpen: false,
}

/** Opções dos filtros da página (`GET /occurrences/processes/filter-options`). */
export interface FilterOptions {
  analysts: AnalystSelection
  stages: string[]
}
/** Colunas ordenáveis da visão "por ocorrência" — do backend. */
export type { OccurrenceSortKey }
export type ProcessSortKey =
  | 'process_number'
  | 'company'
  | 'occurrences'
  | 'deadline'

export interface OccurrenceListItem {
  id: number
  title: string
  status: 'open' | 'done'
  /** Categoria da ocorrência (`occurrence_meta.categoria`). */
  categoria: ProcessSubjectTopic | null
  prazo: string | null
  is_overdue: boolean
  created_at: string
  completed_at: string | null
  created_by_name: string | null
  responsible_id: number | null
  responsible_name: string | null
  process_id: number
  process_number: string | null
  company: string | null
  status_processo: string | null
  is_critical: boolean
  last_message_at: string | null
  unread_count: number
}

export interface OccurrenceListResponse {
  items: OccurrenceListItem[]
  /** Contadores dos blocos — independem do bloco escolhido (só busca +
   * empresas). `responsible`/`overdue` contam só as em andamento. */
  totals: {
    open: number
    done: number
    responsible: number
    overdue: number
    /** Com mensagem nova, em processos que a pessoa segue. */
    unread: number
    /** Prazo da vencida mais antiga ("mais antiga há N dias"). */
    overdue_oldest: string | null
  }
  /** Quantas casam com o bloco escolhido — base da paginação. */
  total: number
  page: number
  limit: number
  has_more: boolean
}

export interface MentionableEmployee {
  id: number
  name: string
  initials: string
  email: string | null
  role: string
  google_picture?: string | null
  microsoft_picture?: string | null
  /**
   * Topics que o tipo libera; `null` = sem restrição. Cruzar com a categoria
   * escolhida via `topicsPermitemAssunto` (responsável de ocorrência).
   */
  allowed_topics?: string[] | null
}

export type StepperItemStatus = 'concluido' | 'em-andamento' | 'pendente'

export interface StepperItem {
  key: string
  title: string
  status: StepperItemStatus
  timestamp: string | null
  observation?: string
}

export interface StepperResponse {
  etd: string | null
  etd_done: boolean
  eta: string | null
  eta_done: boolean
  status_processo: string | null
  steps: StepperItem[]
}

export type ProcessListFilter =
  | 'all'
  | 'mention'
  | 'critical'
  /** Só processos com ocorrência aberta — card "Com ocorrências" da lista de processos. */
  | 'occurrence'
  /** Processos com algum assunto aberto (qualquer tópico). */
  | 'in_progress'
  /** Processos com assunto aberto em que o usuário logado é o responsável. */
  | 'responsible'

export type OccurrenceBatchRowStatus =
  | 'ok'
  | 'aviso'
  | 'erro'
  | 'duplicada'
  | 'falhou'

export interface OccurrenceBatchIssue {
  coluna: string
  motivo: string
}

export interface OccurrenceBatchRow {
  linha: number
  status: OccurrenceBatchRowStatus
  processo: {
    id: number
    process_number: string | null
    house_bl: string | null
  } | null
  titulo: string | null
  categoria: ProcessSubjectCategory | null
  prazo: string | null
  responsavel: { id: number; name: string } | null
  erros: OccurrenceBatchIssue[]
  avisos: OccurrenceBatchIssue[]
  occurrenceId?: number
}

export interface OccurrenceBatchResult {
  resumo: {
    total: number
    ok: number
    aviso: number
    erro: number
    duplicada: number
    falhou: number
    criadas: number
  }
  linhas: OccurrenceBatchRow[]
}
